import { useState } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import api from "../lib/api";
import { Languages, ArrowLeftRight, Loader2, Copy, Check } from "lucide-react";

const LANGS = ["français","anglais","espagnol","allemand","italien","portugais","japonais","chinois","arabe","russe","hindi","néerlandais","coréen","turc"];

export default function TranslatePage() {
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("français");
  const [out, setOut] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const translate = async () => {
    if (!source.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/translate", { text: source, target_lang: target });
      setOut(data.translated);
    } catch {} finally { setLoading(false); }
  };

  const swap = () => {
    setSource(out); setOut(source);
  };

  const copy = () => {
    navigator.clipboard.writeText(out);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-12" data-testid="translate-page">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight flex items-center gap-3 mb-2">
          <Languages size={28} /> Traduire
        </h1>
        <p className="text-sm text-gray-500 mb-6">Propulsé par Claude Sonnet 4.5. Détection de langue automatique.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
          <div className="border border-[var(--omni-border)] rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-2">Texte source</div>
            <Textarea
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="Tapez ou collez votre texte…"
              className="min-h-[260px] resize-none border-0 focus-visible:ring-0 px-0"
              data-testid="translate-source"
            />
            <div className="text-xs text-gray-400 mt-2">{source.length} caractères</div>
          </div>
          <div className="border border-[var(--omni-border)] rounded-lg p-4 bg-[var(--omni-surface)]">
            <div className="flex items-center justify-between mb-2 text-xs text-gray-500">
              <span>Traduction</span>
              <select value={target} onChange={(e) => setTarget(e.target.value)} className="bg-transparent border-0 outline-none text-xs" data-testid="translate-target">
                {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="min-h-[260px] text-base whitespace-pre-wrap" data-testid="translate-output">
              {loading ? <div className="text-gray-500 inline-flex items-center"><Loader2 className="animate-spin mr-2" size={16} /> Traduction…</div> : (out || <span className="text-gray-400">La traduction apparaîtra ici.</span>)}
            </div>
            {out && (
              <div className="text-right mt-2">
                <button onClick={copy} className="inline-flex items-center text-xs gap-1 text-gray-600 hover:text-black" data-testid="translate-copy-btn">
                  {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />} Copier
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex justify-center gap-2">
          <Button variant="outline" onClick={swap} className="rounded-full" data-testid="translate-swap-btn"><ArrowLeftRight size={14} className="mr-1" /> Échanger</Button>
          <Button onClick={translate} disabled={loading || !source.trim()} className="bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white rounded-full px-6" data-testid="translate-btn">
            Traduire
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
